import React, { useState } from 'react'
import { Formik, Form, Field, ErrorMessage } from 'formik'
import * as Yup from 'yup'
import { Button, Spinner } from 'react-bootstrap'
import { withGoogleReCaptcha } from 'react-google-recaptcha-v3'
import { submitSalesForm } from '../../services/sales-form'
import { REGEX_EMAIL } from '../../constants'

import styles from '../Sales.module.css'

const PERSONAL_EMAIL_DOMAINS = [
  'gmail.com',
  'yahoo.com',
  'hotmail.com',
  'outlook.com',
  'aol.com',
  'icloud.com',
  'me.com',
  'mac.com',
  'live.com',
  'msn.com',
  'ymail.com',
  'rocketmail.com',
  'protonmail.com',
  'tutanota.com'
]

const validateBusinessEmail = (email) => {
  if (!email) return false

  if (!REGEX_EMAIL.test(email.toLowerCase())) return false

  const domain = email.toLowerCase().split('@')[1]

  return !PERSONAL_EMAIL_DOMAINS.includes(domain)
}

// Phone number validation (basic international format)
const PHONE_REGEX = /^[\+]?[1-9][\d]{0,15}$/

const URL_REGEX = /^(https?:\/\/)?([\da-z\.-]+)\.([a-z\.]{2,6})([\/\w \.-]*)*\/?$/

const validationSchema = Yup.object({
  name: Yup.string()
    .min(2, 'Name must be at least 2 characters')
    .max(50, 'Name must be less than 50 characters')
    .required('Name is required'),

  businessEmail: Yup.string()
    .email('Please enter a valid email address')
    .test('business-email', 'Please enter a business email address', validateBusinessEmail)
    .required('Business email is required'),

  phoneNumber: Yup.string().matches(PHONE_REGEX, 'Please enter a valid phone number'),

  companyName: Yup.string()
    .min(2, 'Company name must be at least 2 characters')
    .max(100, 'Company name must be less than 100 characters')
    .required('Company name is required'),

  companySize: Yup.string().required('Please select your company size'),

  websiteUrl: Yup.string().matches(URL_REGEX, 'Please enter a valid website URL').required('Website URL is required'),

  reason: Yup.string().required('Please select a reason for contacting us'),

  additionalInfo: Yup.string().max(500, 'Additional information must be less than 500 characters')
})

const COMPANY_SIZE_OPTIONS = [
  { value: '', label: 'Select company size' },
  { value: '1-10', label: 'Small (1-10 employees)' },
  { value: '11-50', label: 'Medium (11-50 employees)' },
  { value: '51-200', label: 'Large (51-200 employees)' },
  { value: '200+', label: 'Enterprise (200+ employees)' }
]

const REASON_OPTIONS = [
  { value: '', label: 'Select reason for contact' },
  { value: 'explore', label: 'Discuss business or enterprise use case' },
  { value: 'buy', label: 'Ready to purchase an enterprise plan' },
  { value: 'pricing', label: 'Explore pricing options' },
  { value: 'demo', label: 'Product demo' },
  { value: 'support', label: 'Support/Billing issues' },
  { value: 'other', label: 'Other' }
]

const initialValues = {
  name: '',
  businessEmail: '',
  phoneNumber: '',
  companyName: '',
  companySize: '',
  websiteUrl: '',
  reason: '',
  additionalInfo: ''
}

const SalesForm = ({ googleReCaptchaProps }) => {
  const [submitStatus, setSubmitStatus] = useState(null) // 'success', 'error', or null
  const [isSubmitting, setIsSubmitting] = useState(false)

  const handleSubmit = async (values, { resetForm }) => {
    setIsSubmitting(true)
    setSubmitStatus(null)

    try {
      // Execute reCaptcha before form submission
      const recaptchaValue = await googleReCaptchaProps.executeRecaptcha('sales_form')
      const headers = { 'x-recaptcha': recaptchaValue }

      await submitSalesForm(values, headers)
      setSubmitStatus('success')
      resetForm()
    } catch (error) {
      console.error('Sales form submission error:', error)
      setSubmitStatus('error')
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div>
      <h2 className={styles.formTitle}>Get in Touch</h2>
      <p className={styles.formSubtitle}>Our sales team will get back to you within 24 hours.</p>

      {submitStatus === 'success' && (
        <div className={styles.successMessage}>
          Thank you for your interest! Our sales team will contact you within 24 hours.
        </div>
      )}

      {submitStatus === 'error' && (
        <div className={styles.errorAlert}>
          Sorry, there was an error submitting your form. Please try again or contact us directly (hello@tiiny.host).
        </div>
      )}

      <Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={handleSubmit}>
        {({ isValid, dirty }) => (
          <Form>
            <div className={styles.formGroup}>
              <Field type="text" id="name" name="name" className={styles.formInput} placeholder="Full name" />
              <ErrorMessage name="name" component="div" className={styles.errorMessage} />
            </div>

            <div className={styles.formGroup}>
              <Field
                type="email"
                id="businessEmail"
                name="businessEmail"
                className={styles.formInput}
                placeholder="Business email"
              />
              <ErrorMessage name="businessEmail" component="div" className={styles.errorMessage} />
            </div>

            <div className={styles.formGroup}>
              <Field
                type="tel"
                id="phoneNumber"
                name="phoneNumber"
                className={styles.formInput}
                placeholder="Phone number"
              />
              <ErrorMessage name="phoneNumber" component="div" className={styles.errorMessage} />
            </div>

            <div className={styles.formGroup}>
              <Field
                type="text"
                id="companyName"
                name="companyName"
                className={styles.formInput}
                placeholder="Company name"
              />
              <ErrorMessage name="companyName" component="div" className={styles.errorMessage} />
            </div>

            <div className={styles.formGroup}>
              <Field as="select" id="companySize" name="companySize" className={styles.formSelect}>
                {COMPANY_SIZE_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Field>
              <ErrorMessage name="companySize" component="div" className={styles.errorMessage} />
            </div>

            <div className={styles.formGroup}>
              <Field
                type="text"
                id="websiteUrl"
                name="websiteUrl"
                className={styles.formInput}
                placeholder="Website URL"
              />
              <ErrorMessage name="websiteUrl" component="div" className={styles.errorMessage} />
            </div>

            <div className={styles.formGroup}>
              <Field as="select" id="reason" name="reason" className={styles.formSelect}>
                {REASON_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Field>
              <ErrorMessage name="reason" component="div" className={styles.errorMessage} />
            </div>

            <div className={styles.formGroup}>
              <Field
                as="textarea"
                id="additionalInfo"
                name="additionalInfo"
                className={styles.formTextarea}
                placeholder="Any additional information (optional)"
              />
              <ErrorMessage name="additionalInfo" component="div" className={styles.errorMessage} />
            </div>

            <Button type="submit" size="lg" className="w-100" disabled={isSubmitting || !isValid || !dirty}>
              {isSubmitting ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  Submitting...
                </>
              ) : (
                'Submit Request'
              )}
            </Button>
          </Form>
        )}
      </Formik>
    </div>
  )
}

export default withGoogleReCaptcha(SalesForm)
